import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";

const lines = [
  { text: "HABILLÉ", cls: "text-foreground" },
  { text: "POUR LA", cls: "text-outline" },
  { text: "NUIT.", cls: "text-blood" },
];

const MaskedLine = ({ children, index }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay: 0.25 + index * 0.14, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgTextY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden grain pt-16">
      <motion.span
        style={{ y: bgTextY }}
        className="pointer-events-none select-none absolute -bottom-8 left-0 font-syne font-extrabold uppercase text-[15vw] leading-none text-white/[0.03] tracking-tighter"
      >
        TWELVE
      </motion.span>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full grid lg:grid-cols-12 gap-10 items-center relative z-10 py-16">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="flex items-center gap-3 mb-8"
          >
            <span data-testid="hero-drop-status" className="flex items-center gap-2 border border-volt/40 px-3 py-1.5 font-mono text-[11px] tracking-[0.2em] uppercase text-volt">
              <span className="w-1.5 h-1.5 bg-volt animate-pulse-dot" />
              Drop 04 • En stock
            </span>
            <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-ash">FW26 — Nocturne</span>
          </motion.div>

          <h1 className="font-syne font-extrabold uppercase tracking-tighter leading-[0.92] text-6xl sm:text-7xl lg:text-8xl xl:text-9xl">
            {lines.map((l, i) => (
              <MaskedLine key={l.text} index={i}>
                <span className={l.cls}>{l.text}</span>
              </MaskedLine>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-8 max-w-md text-smoke text-base leading-relaxed"
          >
            Streetwear technique & sneakers en édition limitée. Conçu dans l'ombre,
            porté sous les néons. Chaque pièce est numérotée, aucune n'est reproduite.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/boutique"
              data-testid="hero-cta-shop"
              className="group inline-flex items-center gap-3 bg-blood text-white font-mono text-xs uppercase tracking-[0.2em] px-8 py-4 hover:bg-white hover:text-ink transition-colors"
            >
              Explorer le drop
              <ArrowDownRight size={16} className="transition-transform group-hover:translate-x-1 group-hover:translate-y-1" />
            </Link>
            <a
              href="/#manifeste"
              data-testid="hero-cta-manifesto"
              className="inline-flex items-center gap-3 border border-white/20 font-mono text-xs uppercase tracking-[0.2em] px-8 py-4 hover:border-volt hover:text-volt transition-colors"
            >
              Le manifeste
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5 relative"
        >
          <div className="relative clip-frame overflow-hidden border border-white/10">
            <motion.img
              style={{ y: imgY }}
              src="https://images.unsplash.com/photo-1598539962077-e4185f37104f?auto=format&fit=crop&w=1000&q=80"
              alt="Look streetwear Nocturne"
              data-testid="hero-image"
              className="w-full h-[420px] lg:h-[560px] object-cover scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
            <div className="absolute bottom-4 left-4 font-mono text-[10px] tracking-[0.25em] uppercase text-white/80">
              Look 01 — Hoodie Nocturne
            </div>
          </div>
          <div className="absolute -top-4 -right-4 bg-volt text-ink font-mono text-[11px] font-bold uppercase tracking-[0.15em] px-4 py-2 rotate-3">
            Édition limitée
          </div>
        </motion.div>
      </div>
    </section>
  );
};
